import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatPrice, sanitizeHtml } from "../lib/api";
import { ChevronLeft, Box, Shield, Truck, MessageCircle, Phone } from "lucide-react";

export default function ProductDetailPage() {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(0);
    const [related, setRelated] = useState([]);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        setLoading(true);
        api.get(`/products/${id}`)
            .then((r) => {
                setProduct(r.data);
                if (r.data.category_id) {
                    api.get("/products", { params: { category_id: r.data.category_id, limit: 4 } })
                        .then((rr) => setRelated((rr.data.items || []).filter((p) => p.id !== r.data.id).slice(0, 3)));
                }
            })
            .catch(() => setProduct(null))
            .finally(() => setLoading(false));
        api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    }, [id]);

    if (loading) return <div className="max-w-7xl mx-auto px-4 py-20 text-center font-mono text-sm">Cargando…</div>;
    if (!product) return (
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
            <div className="font-display text-3xl font-bold uppercase">Producto no encontrado</div>
            <Link to="/productos" className="lz-btn lz-btn-primary mt-6 inline-flex">Ver catálogo</Link>
        </div>
    );

    const images = product.images?.length ? product.images : [];
    const wa = (settings?.whatsapp || "").replace(/[^0-9]/g, "");
    const waLink = wa
        ? `https://wa.me/${wa}?text=${encodeURIComponent(`Hola, me interesa el producto: ${product.name}${product.sku ? ` (SKU: ${product.sku})` : ""}.`)}`
        : null;
    const phone = settings?.phone;

    return (
        <div className="max-w-7xl mx-auto px-4 py-8" data-testid="product-detail">
            <Link to="/productos" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold mb-6 hover:text-[var(--lz-orange)]" data-testid="back-to-catalog">
                <ChevronLeft className="w-4 h-4" /> Volver al catálogo
            </Link>
            <div className="grid lg:grid-cols-12 gap-10">
                {/* Gallery */}
                <div className="lg:col-span-7 grid grid-cols-12 gap-3">
                    <div className="col-span-2 flex flex-col gap-2">
                        {images.map((img, i) => (
                            <button
                                key={`thumb-${img.slice(-32)}-${i}`}
                                type="button"
                                onClick={() => setActive(i)}
                                className={`aspect-square bg-[#f3f4f6] border-2 ${active === i ? "border-[var(--lz-orange)]" : "border-[var(--lz-navy)]/20"} hover:border-[var(--lz-orange)]`}
                                data-testid={`gallery-thumb-${i}`}
                            >
                                <img src={img} alt={`thumb ${i}`} className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                    <div className="col-span-10 aspect-square bg-[#f3f4f6] border-2 border-[var(--lz-navy)] overflow-hidden">
                        {images[active] ? (
                            <img src={images[active]} alt={product.name} className="w-full h-full object-cover" data-testid="gallery-main" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-[var(--lz-navy)]/30"><Box className="w-24 h-24" /></div>
                        )}
                    </div>
                </div>
                {/* Info */}
                <div className="lg:col-span-5">
                    {product.brand && (
                        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--lz-orange)] mb-2">{product.brand}</div>
                    )}
                    <h1 className="font-display text-3xl md:text-4xl font-bold uppercase leading-tight" data-testid="product-name">{product.name}</h1>
                    {product.sku && <div className="font-mono text-xs text-[var(--lz-navy)]/60 mt-2">SKU: {product.sku}</div>}
                    <div className="font-display text-5xl font-bold mt-6" data-testid="product-price">{formatPrice(product.price)}</div>

                    <div className="mt-6 prose-lz text-sm text-[var(--lz-navy)]/80" dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description) }} data-testid="product-description" />

                    <div className="mt-8 space-y-2">
                        {waLink && (
                            <a href={waLink} target="_blank" rel="noreferrer" className="lz-btn lz-btn-primary w-full" data-testid="contact-whatsapp">
                                <MessageCircle className="w-4 h-4" /> Consultar por WhatsApp
                            </a>
                        )}
                        {phone && (
                            <a href={`tel:${phone}`} className="lz-btn lz-btn-outline w-full" data-testid="contact-phone">
                                <Phone className="w-4 h-4" /> Llamar a {phone}
                            </a>
                        )}
                        <Link to="/contacto" className="lz-btn lz-btn-ghost w-full text-sm" data-testid="contact-page-link">
                            Visítanos en sucursal
                        </Link>
                    </div>

                    <div className="mt-8 grid grid-cols-3 gap-3 text-center text-xs uppercase tracking-wider font-bold">
                        <div className="p-3 border border-[var(--lz-navy)]"><Truck className="w-5 h-5 mx-auto mb-1 text-[var(--lz-orange)]" />Entrega rápida</div>
                        <div className="p-3 border border-[var(--lz-navy)]"><Shield className="w-5 h-5 mx-auto mb-1 text-[var(--lz-orange)]" />Garantía</div>
                        <div className="p-3 border border-[var(--lz-navy)]"><Box className="w-5 h-5 mx-auto mb-1 text-[var(--lz-orange)]" />Original</div>
                    </div>

                    {Object.keys(product.specifications || {}).length > 0 && (
                        <div className="mt-8 border-2 border-[var(--lz-navy)]">
                            <div className="bg-[var(--lz-navy)] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest">Especificaciones</div>
                            <table className="w-full text-sm" data-testid="product-specs">
                                <tbody>
                                    {Object.entries(product.specifications).map(([k, v], idx) => (
                                        <tr key={k} className={idx % 2 ? "bg-[#f3f4f6]" : ""}>
                                            <td className="py-2 px-4 font-bold uppercase text-xs w-1/3 border-t border-[var(--lz-navy)]/20">{k}</td>
                                            <td className="py-2 px-4 border-t border-[var(--lz-navy)]/20">{String(v)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {related.length > 0 && (
                <section className="mt-16">
                    <h2 className="font-display text-2xl font-bold uppercase mb-6">También te puede interesar</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {related.map((p) => (
                            <Link key={p.id} to={`/producto/${p.id}`} className="lz-card block" data-testid={`related-${p.id}`}>
                                <div className="aspect-square bg-[#f3f4f6] border-b border-[var(--lz-navy)] overflow-hidden">
                                    {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                                </div>
                                <div className="p-4">
                                    <div className="font-bold uppercase line-clamp-2 text-sm">{p.name}</div>
                                    <div className="font-display text-xl font-bold mt-2">{formatPrice(p.price)}</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
